<script setup>
import { computed, ref, watch } from 'vue';
import { PageHeader, useSecondaryNavigationPanel } from '@dashhudson/dashing-ui';
import { useRoute, useRouter } from 'vue-router';
import merge from 'lodash/merge';

const props = defineProps({
  title: { type: String, required: true },
  // Remove this hideMarginClass prop in this ticket: https://app.shortcut.com/dashhudson/story/138793/remove-top-padding
  hideMarginClass: { type: Boolean, default: false },
  tooltip: { type: String, default: null },
  tabs: { type: Array, default: null },
});

const { expanded } = useSecondaryNavigationPanel();
const route = useRoute();
const router = useRouter();

const secondaryNavExpanded = computed(() => {
  if (route?.meta?.hideSecondaryNav) {
    return false;
  }
  return expanded.value;
});

const isReportRoute = computed(() => route?.meta?.report !== undefined);

const selectedTab = ref();

function onTabClick(tabValue) {
  if (selectedTab.value === tabValue) {
    return;
  }

  selectedTab.value = tabValue;

  const tab = props.tabs.find((t) => t.value === tabValue);
  if (tab?.to) {
    router.push(merge({}, { query: route.query }, tab.to));
  }
}

function isTabActive(tab) {
  if (!tab?.to || !route) return false;

  const target = router.resolve(tab.to);

  if (target.name !== route.name) return false;

  const targetQuery = target.query || {};
  const currentQuery = route.query || {};

  return Object.entries(targetQuery).every(([key, expected]) => {
    const actualValue = currentQuery[key];
    return String(actualValue ?? '') === String(expected ?? '');
  });
}

function updateSelectedTab() {
  selectedTab.value = props.tabs?.find(isTabActive)?.value ?? null;
}

watch(
  () => [route?.name, route?.query, props.tabs],
  () => updateSelectedTab(),
  { immediate: true, deep: true },
);
</script>
<template>
  <PageHeader
    :class="[props.hideMarginClass || isReportRoute ? '' : '-mt-10']"
    :title="props.title"
    :rounded="!secondaryNavExpanded"
    :tooltip="props.tooltip"
    :tabs="props.tabs"
    :tab="selectedTab"
    @update:tab="onTabClick"
  >
    <template #actions>
      <slot name="actions" />
    </template>
    <slot />
  </PageHeader>
</template>
